import type { ProjectDTO } from '@devrobot/shared';
import { Database, ExternalLink, Tag } from 'lucide-react';
import { useReleases } from '../../api/queries';
import { CopyButton } from '../../components/CopyButton';
import { EmptyState } from '../../components/EmptyState';
import { ErrorAlert } from '../../components/ErrorAlert';
import { RelativeTime } from '../../components/RelativeTime';
import { SkeletonTable } from '../../components/Skeleton';
import { shortDigest } from '../../lib/format';

type Release = NonNullable<ReturnType<typeof useReleases>['data']>[number];

function Pipeline({ r }: { r: Release }) {
  return r.pipelineUrl ? (
    <a className="link inline-flex items-center gap-1" href={r.pipelineUrl} target="_blank" rel="noreferrer">
      #{r.pipelineId} <ExternalLink className="size-3" aria-hidden="true" />
    </a>
  ) : (
    <>—</>
  );
}

function Migrations({ r }: { r: Release }) {
  return r.migrations ? (
    <span className="badge badge-soft badge-warning badge-sm gap-1">
      <Database className="size-3" aria-hidden="true" />
      ran migrations
    </span>
  ) : (
    <span className="text-sm text-base-content/60">none</span>
  );
}

function Digests({ r }: { r: Release }) {
  return (
    <ul className="space-y-0.5 text-xs">
      {Object.entries(r.imageDigests).map(([svc, d]) => (
        <li key={svc} className="flex items-center gap-1 font-mono" title={d}>
          <span className="text-base-content/60">{svc}</span> {shortDigest(d)}
          <CopyButton value={d} label={`Copy ${svc} image digest`} />
        </li>
      ))}
    </ul>
  );
}

export function ReleasesTab({ project }: { project: ProjectDTO }) {
  const q = useReleases(project.key);
  if (q.isPending) return <SkeletonTable />;
  if (q.isError) return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  if (q.data.length === 0) {
    return (
      <EmptyState icon={Tag} title="No releases yet">
        A release is recorded for every deployed commit: its branch, GitLab pipeline and image digests. Deploy once to create the first.
      </EmptyState>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-base-content/65">Releases are immutable: a commit, the pipeline that built it, and the image digests that were deployed. Rollbacks select one of these.</p>
      <div className="card dr-surface overflow-hidden">
        {/* Phones: stacked rows instead of a sideways-scrolling table. */}
        <ul className="divide-y divide-hairline md:hidden">
          {q.data.map((r) => (
            <li key={r.id} className="space-y-2 p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <span className="inline-flex items-center gap-1 font-mono text-sm font-medium">
                    {r.shortSha}
                    <CopyButton value={r.commitSha} label="Copy full commit SHA" />
                  </span>
                  {r.commitTitle ? <div className="text-sm text-base-content/65">{r.commitTitle}</div> : null}
                </div>
                <Migrations r={r} />
              </div>
              <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-base-content/60">
                <span className="font-mono">{r.branch}</span>
                <span aria-hidden="true">·</span>
                <span>
                  Pipeline <Pipeline r={r} />
                </span>
                <span aria-hidden="true">·</span>
                <RelativeTime value={r.createdAt} />
              </p>
              <Digests r={r} />
            </li>
          ))}
        </ul>
        <div className="overflow-x-auto max-md:hidden">
          <table className="table table-sm">
            <thead>
              <tr className="text-xs tracking-wide text-base-content/50 uppercase">
                <th>Commit</th>
                <th>Branch</th>
                <th>Pipeline</th>
                <th>Images</th>
                <th>Migrations</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {q.data.map((r) => (
                <tr key={r.id} className="hover:bg-base-200/50">
                  <td className="min-w-48">
                    <span className="inline-flex items-center gap-1 font-mono text-sm font-medium">
                      {r.shortSha}
                      <CopyButton value={r.commitSha} label="Copy full commit SHA" />
                    </span>
                    {r.commitTitle ? <div className="text-xs text-base-content/65">{r.commitTitle}</div> : null}
                  </td>
                  <td className="font-mono text-sm">{r.branch}</td>
                  <td className="text-sm">
                    <Pipeline r={r} />
                  </td>
                  <td>
                    <Digests r={r} />
                  </td>
                  <td>
                    <Migrations r={r} />
                  </td>
                  <td className="text-sm whitespace-nowrap">
                    <RelativeTime value={r.createdAt} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
